import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Info, IndianRupee, TrendingUp, Receipt, Percent, Moon, BedDouble, Wand2, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import OpsStrip from "@/components/workspace/OpsStrip";

const fmt = (n) => "₹" + Number(n).toLocaleString("en-IN");

const StatCard = ({ icon: Icon, label, value, trace, tone = "text-slate-900", testid }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-5 relative">
      <div className="flex items-center justify-between">
        <div className="w-9 h-9 rounded-xl bg-[#EEF2FF] flex items-center justify-center"><Icon size={16} className="brand-pink" /></div>
        <button data-testid={`${testid}-trace`} onClick={() => setOpen(!open)} title="How is this calculated?"
          className="text-slate-300 hover:brand-pink transition-colors"><Info size={15} /></button>
      </div>
      <p className={`font-display font-extrabold text-2xl mt-3 ${tone}`}>{value}</p>
      <p className="text-[11px] text-slate-400 font-medium mt-0.5">{label}</p>
      {open && (
        <div className="absolute left-2 right-2 top-full mt-1 z-20 bg-slate-900 text-white text-[11px] leading-relaxed rounded-xl p-3.5 shadow-2xl">
          <p className="font-semibold text-[10px] uppercase tracking-wider text-slate-400 mb-1">How is this calculated?</p>
          {trace}
        </div>
      )}
    </div>
  );
};

export default function DashboardTab({ pid, month, refreshKey, onSeed, seeding }) {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/pms/properties/${pid}/dashboard`, { params: { month } })
      .then(({ data }) => setD(data)).catch((e) => toast.error(errMsg(e))).finally(() => setLoading(false));
  }, [pid, month, refreshKey]);

  if (loading || !d) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-300" size={30} /></div>;

  const noData = d.bookings_count === 0 && d.revenue === 0;
  const maxCh = Math.max(...d.channel_revenue.map((c) => c.revenue), 1);

  return (
    <div className="space-y-6 reveal">
      <OpsStrip pid={pid} refreshKey={refreshKey} />

      {noData && (
        <div data-testid="no-bookings-banner" className="bg-[#EEF2FF] border border-[#C7D2FE] rounded-2xl px-6 py-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display font-bold text-slate-900">No bookings in this month yet</p>
            <p className="text-xs text-slate-500 mt-0.5">Pull in realistic demo bookings from Atiti, Booking.com & Airbnb to see the P&L come alive.</p>
          </div>
          <button data-testid="seed-bookings-button" onClick={onSeed} disabled={seeding}
            className="btn-brand rounded-full px-5 py-2.5 text-xs font-semibold flex items-center gap-2">
            {seeding ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />} Seed demo bookings
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <StatCard testid="stat-revenue" icon={IndianRupee} label="Revenue" value={fmt(d.revenue)} trace={d.traces.revenue} />
        <StatCard testid="stat-expenses" icon={Receipt} label="Total costs (expenses + subscription)" value={fmt(d.expenses + d.subscription)} trace={`${d.traces.expenses} — plus ${d.traces.subscription}`} />
        <StatCard testid="stat-profit" icon={TrendingUp} label="Net profit" value={fmt(d.profit)} tone={d.profit >= 0 ? "text-emerald-600" : "text-red-500"} trace={d.traces.profit} />
        <StatCard testid="stat-occupancy" icon={Percent} label="Occupancy" value={`${d.occupancy}%`} trace={d.traces.occupancy} />
        <StatCard testid="stat-adr" icon={Moon} label="ADR / night" value={fmt(d.adr)} trace={d.traces.adr} />
        <StatCard testid="stat-nights" icon={BedDouble} label="Booked nights" value={d.booked_nights} trace={`${d.bookings_count} bookings touching ${d.month} contribute ${d.booked_nights} unit-nights (deduplicated per unit per date).`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 text-sm">Revenue by channel</h3>
          <div className="space-y-4 mt-5">
            {d.channel_revenue.length === 0 && <p className="text-xs text-slate-400">No channel revenue this month.</p>}
            {d.channel_revenue.map((c) => (
              <div key={c.channel}>
                <div className="flex justify-between text-xs mb-1.5">
                  <span className="font-semibold text-slate-700">{c.channel}</span>
                  <span className="font-mono text-slate-500">{fmt(c.revenue)}</span>
                </div>
                <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${(c.revenue / maxCh) * 100}%`, backgroundColor: c.channel === "Atiti" ? "#4F46E5" : c.channel === "Booking.com" ? "#0EA5E9" : "#8B5CF6" }} />
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-slate-400 mt-5">Atiti bookings carry 0% host commission — every rupee shown is yours.</p>
        </div>

        <div className="lg:col-span-3 bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-display font-bold text-slate-900 text-sm">Per-unit performance — {new Date(`${d.month}-01T00:00:00`).toLocaleString("en-IN", { month: "long", year: "numeric" })}</h3>
          <div className="overflow-x-auto mt-3">
            <table className="w-full text-sm">
              <thead><tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="text-left py-2 font-semibold">Unit</th><th className="text-right py-2 font-semibold">Nights</th>
                <th className="text-right py-2 font-semibold">Occupancy</th><th className="text-right py-2 font-semibold">Revenue</th>
              </tr></thead>
              <tbody>
                {d.unit_rows.map((r) => (
                  <tr key={r.unit_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                    <td className="py-2.5"><span className="h-6 px-2 rounded-md bg-slate-900 text-white font-display font-bold text-[11px] inline-flex items-center">{r.name}</span></td>
                    <td className="py-2.5 text-right font-mono text-slate-600">{r.nights}</td>
                    <td className="py-2.5 text-right">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden inline-block"><span className="h-full bg-brand rounded-full block" style={{ width: `${Math.min(r.occupancy, 100)}%` }} /></span>
                        <span className="font-mono text-slate-600 text-xs w-11 text-right">{r.occupancy}%</span>
                      </span>
                    </td>
                    <td className="py-2.5 text-right font-mono text-slate-700">{fmt(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <GrowthAdvisorCard pid={pid} />
    </div>
  );
}

function GrowthAdvisorCard({ pid }) {
  const [suggestions, setSuggestions] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/pms/properties/${pid}/growth-advisor`);
      setSuggestions(data.suggestions || []);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="mt-6 bg-gradient-to-br from-indigo-50 to-white border border-indigo-200 rounded-2xl p-6" data-testid="growth-advisor-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#4F46E5] flex items-center justify-center shrink-0">
          <Sparkles size={18} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-bold text-slate-900">Growth Advisor</h3>
          <p className="text-xs text-slate-500 mt-0.5">AI reads this property's monthly P&L and suggests concrete growth actions.</p>
        </div>
        <button data-testid="run-advisor-btn" onClick={run} disabled={busy}
          className="flex items-center gap-2 bg-[#4F46E5] hover:bg-[#4338CA] text-white text-sm font-semibold rounded-full px-5 py-2 disabled:opacity-50">
          {busy ? <Loader2 className="animate-spin" size={14} /> : <Sparkles size={14} />}
          {suggestions ? "Refresh advice" : "Get advice"}
        </button>
      </div>
      {suggestions && (
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3">
          {suggestions.map((s, i) => (
            <div key={i} className="bg-white border border-indigo-100 rounded-xl p-4">
              <p className="font-semibold text-sm text-slate-900 flex items-start gap-2">
                <span className="text-[#4F46E5]">{i + 1}.</span>
                <span>{s.headline}</span>
              </p>
              <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
